<script lang="ts">
	import { devices_settings } from 'src/stores/device-runtime/status';
	import { readDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import {
		get_gen_2_component_channel_structure,
		get_gen_2_component_from_category,
		real_id,
		get_device_component_index,
	} from '../../../../helpers/interface_helpers';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from '../../../../helpers/utilities';
	//Stores
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	//Design system components
	import { Alert, Button, Input, Text, toast } from '@Shelly/shelly-design-system';

	export let device;

	let id = real_id(device.id);

	let settings = readDeviceSettings(id);

	let rangeMap = settings?.[`${get_gen_2_component_channel_structure(device)}`]?.range_map || [0, 100]; //adding default values to prevent undefined and to avoid non rendering of the component
	let cacheRange = rangeMap;
	let rangeMapMin = rangeMap[0];
	let rangeMapMax = rangeMap[1];

	let loading = false;

	const min = 0;
	const max = 100;

	const setRangeMap = async () => {
		if (cacheRange[0] === rangeMapMin && cacheRange[1] === rangeMapMax) return;
		if (rangeMapMin < min || rangeMapMax > max) {
			return toast.open({
				type: 'danger',
				title:
					$format('movement_limit_toast', {
						values: { min: min, max: max },
					}) || 'Values must be between min - max!',
			});
		}
		loading = true;

		const params = {
			id: get_device_component_index(device),
			config: {
				range_map: [+rangeMapMin, +rangeMapMax],
			},
		};

		const res: SettingRequest = await gen2Request(
			device,
			`${get_gen_2_component_from_category(device)}.SetConfig`,
			params,
		);
		const response = onSettingsResponse(res, device);
		if (response) {
			settings[`${get_gen_2_component_channel_structure(device)}`].range_map = [
				rangeMapMin,
				rangeMapMax,
			];
			devices_settings.update_devices_settings(id, settings);
			cacheRange = [rangeMapMin, rangeMapMax];
		} else {
			rangeMapMin = cacheRange[0];
			rangeMapMax = cacheRange[1];
		}
		loading = false;
	};
	const validate = () => {
		if (+rangeMapMax <= +rangeMapMin) {
			rangeMapMax = +rangeMapMin + 1;
		}
		if (+rangeMapMax >= max) rangeMapMax = max;
		if (+rangeMapMin <= min) rangeMapMin = min;
		if (+rangeMapMin >= max) rangeMapMin = +rangeMapMax - 1;
		//prevent sending lower max value compared to to min value. Also if min value increases max value will be plus 1. And limitations between min and max
	};

	$: rangeMapMinData = getErrorOrInfoMessageForMinAndMax(rangeMapMin, min, max);
	$: rangeMapMaxData = getErrorOrInfoMessageForMinAndMax(rangeMapMax, min, max);
</script>

<Alert
	class="mb-s"
	type="warning"
	heading={$format('alert_heading_warning_requires_reboot', {
		default: 'Changing these settings requires a device reboot!',
	})}
/>
<Alert
	class="mb-s"
	heading={$format('alert_heading_range_map', {
		default: `Remaps output 0%-100% range to the specified values here.`,
	})}
/>
<Text styleType="body-small" class="mb-xs">
	{$format('min')}
</Text>
<Input
	class="mb-s"
	size="m"
	bind:value={rangeMapMin}
	type={'number'}
	{min}
	max={max - 1}
	label={$format('min')}
	error={rangeMapMinData.error}
	info={rangeMapMinData.info}
	step={1}
	on:change={validate}
	on:enter={setRangeMap}
	id="input-ddth9s9"
	aria-labelledby="input-ddth9s9"
/>
<Text styleType="body-small" class="mb-xs">
	{$format('max')}
</Text>
<Input
	size="m"
	bind:value={rangeMapMax}
	type={'number'}
	{min}
	{max}
	label={$format('max')}
	error={rangeMapMaxData.error}
	info={rangeMapMaxData.info}
	step={1}
	on:change={validate}
	on:enter={setRangeMap}
	id="input-no3bmte"
	aria-labelledby="input-no3bmte"
/>
<Button class="mt-s" {loading} size="m" width="full" on:click={setRangeMap}>
	{$format('button_save')}
</Button>
